import { Injectable } from '@angular/core';
import { ApiService } from '@portal/app/shared/services/api.service';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { AuthService } from '@portal/app/shared/services/auth.service';
import { UserModel, UserType } from '@portal/app/shared/models/userModel';
import {
  ConfigDashboardInstance,
  CurrentStatus
} from '@portal/app/shared/types';
import { Observable, firstValueFrom, map, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ConfigDashboardService {
  constructor(
    private readonly apiService: ApiService,
    private readonly selectionService: SelectionService,
    private readonly authService: AuthService
  ) {}

  /**
   * Single source of truth for config-dashboard visibility:
   * ACTIVE is visible to everyone, DRAFT only to internal users, anything else
   * (including INACTIVE, which the API never returns) is hidden.
   */
  public static isInstanceVisible(
    instance: ConfigDashboardInstance | undefined,
    user: UserModel | null
  ): boolean {
    if (!instance) {
      return false;
    }
    if (instance.status === CurrentStatus.active) {
      return true;
    }
    if (instance.status === CurrentStatus.draft) {
      return user?.getUserType() === UserType.internal;
    }
    return false;
  }

  /**
   * Fetches config dashboards for the current selection and resolves whether the
   * dashboard for the given literalId is visible to the current user.
   */
  public isConfigDashboardVisible(
    configDashboards: Record<string, ConfigDashboardInstance>,
    literalId: string
  ): boolean {
    const user = this.authService.getUser() as UserModel | null;
    return ConfigDashboardService.isInstanceVisible(
      configDashboards[literalId],
      user
    );
  }

  public async isDashboardVisible(literalId: string): Promise<boolean> {
    const configDashboards = await this.getConfigDashboardsByLiteralId();
    return this.isConfigDashboardVisible(configDashboards, literalId);
  }

  public async getConfigDashboardsByLiteralId(): Promise<
    Record<string, ConfigDashboardInstance>
  > {
    const mappedConfigDashboards: Record<string, ConfigDashboardInstance> = {};

    const configDashboards = await firstValueFrom(
      this.getConfigDashboards().pipe(take(1))
    );

    configDashboards.forEach(
      (dashboardInstance) =>
        (mappedConfigDashboards[dashboardInstance.literalId] =
          dashboardInstance)
    );

    return mappedConfigDashboards;
  }

  public getConfigDashboards(): Observable<ConfigDashboardInstance[]> {
    const selection = this.selectionService.getSelection();
    const brandId = selection.brand?.brandId;
    const clientId = selection.brand?.clientId;

    return this.apiService.getConfigDashboards({ brandId, clientId }).pipe(
      catchError((error) => {
        return throwError(error);
      }),
      map((dashboards: ConfigDashboardInstance[]) => {
        return dashboards.map((dashboard) => {
          dashboard.literalId = dashboard.configDashboard.literalId;
          return dashboard;
        });
      })
    );
  }
}
